import React from 'react';
import { Building2, UtensilsCrossed, Coffee, CarFront, Shirt, HeartPulse, ArrowRight, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';

const SECTORS = [
  {
    id: 'hotels',
    icon: Building2,
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=900&q=80',
    fr: {
      title: 'Hôtels & Resorts',
      desc: 'Formules dédiées à l\'hôtellerie premium : lessive linge délicat, produits de salle de bain, soins corps & cheveux pour amenities, désinfection chambres et zones communes.',
      tags: ['Linge fin', 'Amenities', 'Hygiène SPA'],
    },
    en: {
      title: 'Hotels & Resorts',
      desc: 'Premium hospitality formulas: delicate fabric care, bathroom products, body & hair care for amenities, room and common-area disinfection.',
      tags: ['Fine linen', 'Amenities', 'SPA hygiene'],
    },
    ar: {
      title: 'الفنادق والمنتجعات',
      desc: 'تركيبات مخصصة للضيافة الفاخرة: عناية بالأقمشة الرقيقة، منتجات الحمام، عناية بالجسم والشعر للمرافق، تعقيم الغرف والمناطق المشتركة.',
      tags: ['أقمشة رقيقة', 'مرافق', 'نظافة سبا'],
    },
  },
  {
    id: 'restaurants',
    icon: UtensilsCrossed,
    image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=900&q=80',
    fr: {
      title: 'Restaurants & HORECA',
      desc: 'Solutions cuisine professionnelle : dégraissants haute performance, lave-vaisselle main et machine, désinfectants surfaces contact alimentaire, désodorisants neutralisants.',
      tags: ['Cuisine pro', 'Norme HACCP', 'Contact alimentaire'],
    },
    en: {
      title: 'Restaurants & HORECA',
      desc: 'Professional kitchen solutions: high-performance degreasers, hand and machine dishwashing, food-contact disinfectants, neutralizing deodorizers.',
      tags: ['Pro kitchen', 'HACCP', 'Food-contact'],
    },
    ar: {
      title: 'المطاعم والمقاهي',
      desc: 'حلول للمطابخ المهنية: مزيلات دهون عالية الأداء، غسيل أطباق يدوي وآلي، معقمات للأسطح الملامسة للطعام، معطرات محايدة للروائح.',
      tags: ['مطبخ مهني', 'HACCP', 'تماس غذائي'],
    },
  },
  {
    id: 'cafes',
    icon: Coffee,
    image: 'https://images.unsplash.com/photo-1554118811-1e0d58224f24?auto=format&fit=crop&w=900&q=80',
    fr: {
      title: 'Cafés & Salons de thé',
      desc: 'Gamme adaptée aux espaces à fort passage : nettoyants multi-surfaces, parfums d\'ambiance Oud & Floral, lave-mains parfumés en grand format, anti-tartre machines à café.',
      tags: ['Ambiance', 'Multi-surfaces', 'Anti-tartre'],
    },
    en: {
      title: 'Cafés & Tea Rooms',
      desc: 'High-traffic ready range: multi-surface cleaners, Oud & Floral air fresheners, large-format scented hand soaps, descalers for coffee machines.',
      tags: ['Ambiance', 'Multi-surface', 'Descaler'],
    },
    ar: {
      title: 'المقاهي وصالونات الشاي',
      desc: 'مجموعة مكيفة للأماكن ذات الحركة الكثيفة: منظفات متعددة الأسطح، معطرات جو عود وزهور، صابون يدين معطر بحجم كبير، مزيلات ترسبات لآلات القهوة.',
      tags: ['أجواء', 'متعدد الأسطح', 'مزيل ترسبات'],
    },
  },
  {
    id: 'carwash',
    icon: CarFront,
    image: 'https://images.unsplash.com/photo-1607860108855-64acf2078ed9?auto=format&fit=crop&w=900&q=80',
    fr: {
      title: 'Stations de Lavage Auto',
      desc: 'Formulations professionnelles haute mousse : shampoing carrosserie, dégraissants moteur, nettoyants jantes, cire de finition. Performance et rendement en bidons 20L.',
      tags: ['Haute mousse', 'Dégraissant moteur', 'Bidons 20L'],
    },
    en: {
      title: 'Car Wash Stations',
      desc: 'High-foam professional formulas: bodywork shampoo, engine degreasers, rim cleaners, finishing wax. Performance and yield in 20L containers.',
      tags: ['High foam', 'Engine degrease', '20L drums'],
    },
    ar: {
      title: 'محطات غسيل السيارات',
      desc: 'تركيبات مهنية برغوة عالية: شامبو الهيكل، مزيلات دهون المحرك، منظفات الجنوط، شمع التلميع. أداء وإنتاجية في عبوات 20 لتر.',
      tags: ['رغوة عالية', 'مزيل دهون', 'عبوات 20 لتر'],
    },
  },
  {
    id: 'laundries',
    icon: Shirt,
    image: 'https://images.unsplash.com/photo-1582735689369-4fe89db7114c?auto=format&fit=crop&w=900&q=80',
    fr: {
      title: 'Pressings & Blanchisseries',
      desc: 'Gel machine concentré 10L, anti-taches professionnel, parfum de linge longue durée, assouplissants premium. Optimisés pour les cycles industriels.',
      tags: ['Concentré', 'Anti-taches', 'Cycles industriels'],
    },
    en: {
      title: 'Dry Cleaners & Laundries',
      desc: 'Concentrated 10L machine gel, professional stain remover, long-lasting laundry perfume, premium softeners. Optimized for industrial cycles.',
      tags: ['Concentrated', 'Stain remover', 'Industrial cycles'],
    },
    ar: {
      title: 'المغاسل والكي',
      desc: 'جل غسالة مركز 10 لتر، مزيل بقع احترافي، عطر غسيل طويل الأمد، ملطفات فاخرة. مُحسَّن للدورات الصناعية.',
      tags: ['مركز', 'مزيل بقع', 'دورات صناعية'],
    },
  },
  {
    id: 'healthcare',
    icon: HeartPulse,
    image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=900&q=80',
    fr: {
      title: 'Cliniques & Centres de soin',
      desc: 'Solutions hygiène hospitalière : désinfectants sols et surfaces, lave-mains antibactériens, javel certifiée, gels désinfectants pour personnel soignant.',
      tags: ['Hygiène pro', 'Antibactérien', 'Désinfectants'],
    },
    en: {
      title: 'Clinics & Healthcare',
      desc: 'Hospital hygiene solutions: floor and surface disinfectants, antibacterial hand soaps, certified bleach, sanitizer gels for healthcare staff.',
      tags: ['Pro hygiene', 'Antibacterial', 'Disinfectants'],
    },
    ar: {
      title: 'العيادات ومراكز الرعاية',
      desc: 'حلول النظافة الاستشفائية: معقمات الأرضيات والأسطح، صابون يدين مضاد للبكتيريا، ماء جافيل معتمد، جل معقم للعاملين الطبيين.',
      tags: ['نظافة احترافية', 'مضاد للبكتيريا', 'معقمات'],
    },
  },
];

const ADVANTAGES = {
  fr: [
    'Formats professionnels (5L, 10L, 20L) — rendement optimisé',
    'Livraison rapide & coaching technique offert',
    'Conditionnement et étiquetage adaptés à votre activité',
    'Fiches techniques et certificats sur demande',
  ],
  en: [
    'Professional formats (5L, 10L, 20L) — optimized yield',
    'Fast delivery & free technical coaching',
    'Packaging and labeling adapted to your activity',
    'Technical sheets and certificates on request',
  ],
  ar: [
    'أحجام احترافية (5، 10، 20 لتر) — إنتاجية مثلى',
    'توصيل سريع وتدريب تقني مجاني',
    'تعبئة وعلامات مكيفة لنشاطكم',
    'بطاقات تقنية وشهادات عند الطلب',
  ],
};

export const IndustrialSection = () => {
  const { language, direction } = useLanguage();

  const t = {
    fr: {
      badge: 'SOLUTIONS B2B & HORECA',
      title: 'Partenaire des Professionnels',
      subtitle: 'Olion accompagne les acteurs industriels et HORECA avec des solutions sur-mesure pour 6 secteurs clés en Tunisie et à l\'international.',
      contactCta: 'Devis professionnel',
      partnerCta: 'Devenir partenaire',
    },
    en: {
      badge: 'B2B & HORECA SOLUTIONS',
      title: 'Professionals\' Partner',
      subtitle: 'Olion supports industrial and HORECA players with tailor-made solutions for 6 key sectors in Tunisia and internationally.',
      contactCta: 'Professional quote',
      partnerCta: 'Become a partner',
    },
    ar: {
      badge: 'حلول B2B والفنادق والمطاعم',
      title: 'شريك المحترفين',
      subtitle: 'تواكب أوليون الفاعلين الصناعيين وقطاع الفنادق والمطاعم بحلول مخصصة لـ 6 قطاعات رئيسية في تونس ودولياً.',
      contactCta: 'عرض احترافي',
      partnerCta: 'كن شريكاً',
    },
  }[language];

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="industriel" className="relative py-20 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 overflow-hidden" dir={direction}>
      {/* Decorative grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
        }}
      />
      {/* Decorative orbs */}
      <div className="absolute top-20 -right-20 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 -left-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <span className="inline-block bg-orange-500/20 backdrop-blur-sm border border-orange-400/30 text-orange-300 px-4 py-2 rounded-full text-sm font-bold tracking-wider uppercase mb-6">
            {t.badge}
          </span>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            {t.title}
          </h2>
          <p className="text-lg sm:text-xl text-blue-100/80 leading-relaxed max-w-3xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        {/* Sector cards grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {SECTORS.map((sector, idx) => {
            const content = sector[language] || sector.fr;
            const Icon = sector.icon;
            return (
              <article
                key={sector.id}
                data-testid={`industrial-${sector.id}`}
                className="group relative bg-white/5 backdrop-blur-md rounded-2xl overflow-hidden border border-white/10 hover:border-orange-400/50 transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl hover:shadow-orange-500/20"
                style={{ animationDelay: `${idx * 100}ms` }}
              >
                {/* Image */}
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={sector.image}
                    alt={content.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent" />
                  {/* Icon badge */}
                  <div className="absolute top-4 left-4 w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg border border-orange-300/30">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-orange-300 transition-colors">
                    {content.title}
                  </h3>
                  <p className="text-sm text-blue-100/70 leading-relaxed mb-4 line-clamp-3">
                    {content.desc}
                  </p>
                  {/* Tags */}
                  <div className="flex flex-wrap gap-2">
                    {content.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full bg-blue-500/10 border border-blue-400/20 text-blue-200"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {/* Advantages strip */}
        <div className="bg-gradient-to-r from-orange-500/10 to-orange-600/10 backdrop-blur-md border border-orange-400/20 rounded-3xl p-8 sm:p-10 mb-12">
          <div className="grid sm:grid-cols-2 gap-x-12 gap-y-4">
            {ADVANTAGES[language].map((adv, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-orange-400 flex-shrink-0 mt-0.5" />
                <span className="text-white text-base leading-relaxed">{adv}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Button
            onClick={() => scrollToSection('contact')}
            data-testid="industrial-cta-contact"
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-10 py-7 rounded-full text-lg font-bold shadow-2xl hover:shadow-orange-500/40 transition-all duration-300 transform hover:scale-105 group"
          >
            {t.contactCta}
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
          <Button
            onClick={() => scrollToSection('partenaires')}
            data-testid="industrial-cta-partner"
            variant="outline"
            className="border-2 border-white/30 bg-white/5 text-white hover:bg-white hover:text-blue-900 px-10 py-7 rounded-full text-lg font-bold backdrop-blur-sm transition-all duration-300 transform hover:scale-105"
          >
            {t.partnerCta}
          </Button>
        </div>
      </div>
    </section>
  );
};
